import { useTrackingInstance } from '@rategravity/1pt-lib';
import { PropsWithTheme } from '@rategravity/own-up-component-library';
import React from 'react';
import { redirects } from '../../data/redirects';
import styled from 'styled-components';

interface AnchorProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  reference: {
    slug: {
      current: string;
    };
  };
}

const WWW = 'www.ownup.com';
const MY = 'my.ownup.com';
const OLD_BLOG = 'resources.ownup.com';
const BLOG_PREFIX = '/learn';

const Link = styled.a`
  color: ${({ theme }: PropsWithTheme<{}>) => theme.underlineLinkColors.linkFontColor};
  text-decoration: none;
`;

const replaceHostUrl = (href: string) => {
  var url = new URL(href);
  return url.pathname;
};

const getDestination = (href?: string) => {
  if (!href) {
    return `https://${process.env.GATSBY_WWW || WWW}/onboarding/get-started`;
  }

  if (href[0] === '/') {
    return href;
  }

  try {
    const { hostname } = new URL(href);

    switch (hostname) {
      case WWW:
        return href.replace(WWW, process.env.GATSBY_WWW || WWW);
      case MY:
        return href.replace(MY, process.env.GATSBY_MY || MY);
      case OLD_BLOG:
        return replaceHostUrl(href);
      default:
        return href;
    }
  } catch (err) {
    console.log(err);
    return href;
  }
};

const getUrlString = (destination: string) => {
  const trailingSlash = '/';

  if (destination && destination.length > 0) {
    if (destination[0] === trailingSlash) {
      return `https://${process.env.GATSBY_WWW}${BLOG_PREFIX}${destination}${
        !destination.endsWith(trailingSlash) ? trailingSlash : ''
      }`;
    } else if (destination.includes(WWW) && !destination.endsWith(trailingSlash)) {
      return destination + trailingSlash;
    }
  }

  return destination;
};

export const replaceRedirects = (inputUrl: string): string => {
  // Check if the input URL contains '/learn'
  if (inputUrl.includes(BLOG_PREFIX)) {
    // Extract the '/learn' and the rest of the string
    const urlParts = inputUrl.split(BLOG_PREFIX);
    const urlToMatch = BLOG_PREFIX + urlParts[1];

    // Check each redirect rule
    for (const redirectRule of redirects) {
      if (urlToMatch === redirectRule.from) {
        // Replace the matched part with the 'to' value
        const newUrl = inputUrl.replace(urlToMatch, redirectRule.to);
        if (newUrl !== inputUrl) {
          return replaceRedirects(newUrl);
        }
        return newUrl;
      }
    }
  }

  // Return the original URL if no match is found
  return inputUrl;
};

const LinkWithTracking = (props: AnchorProps, children: React.ReactNode) => {
  const trackingInstance = useTrackingInstance();
  const destination = getDestination(props?.href || `/${props?.reference?.slug?.current}` || '');
  let urlString = getUrlString(destination || '');
  urlString = replaceRedirects(urlString);

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();

    trackingInstance.track(
      'Blog Post Clicked',
      {
        pageNavigatedTo: { url: urlString }
      },
      'v1'
    );

    setTimeout(() => {
      if (typeof window !== 'undefined' && destination) {
        const url = new URL(urlString);
        window.location.href = url.href;
      }
    }, 300);

    return false;
  };

  return (
    /* eslint-disable-next-line react/jsx-no-target-blank */
    <Link {...props} href={urlString} onClick={onClick} target="_blank">
      {children}
    </Link>
  );
};

export const replaceAnchorTag = (props: AnchorProps, children: React.ReactNode) =>
  LinkWithTracking(props, children);
